import { useState } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function UplistingStatusVocabularyPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    setResults(null);
    try {
      const { data } = await diagnosticsAPI.uplistingStatusVocabulary();
      setResults(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const eventTypes = results?.event_types ? Object.entries(results.event_types) : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          Uplisting Status Vocabulary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Scans <code>uplisting_webhook_logs</code> (last 24 months) to discover
          every distinct value of the payload <code>status</code> field, grouped by
          event type. Confirms whether cancel-signal values appear in{' '}
          <code>booking_updated</code> payloads.
        </p>

        <Button onClick={runAudit} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Run Audit
        </Button>

        {results && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total logs</p>
                <p className="text-xl font-bold tabular-nums">{results.total_logs_scanned}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Event types</p>
                <p className="text-xl font-bold tabular-nums">{eventTypes.length}</p>
              </div>
              <div className={`rounded-lg border p-3 ${results.has_flagged ? 'border-red-500 bg-red-50 dark:bg-red-950/20' : ''}`}>
                <p className="text-xs text-muted-foreground">Uncaught cancel-like</p>
                <p className={`text-xl font-bold tabular-nums ${results.has_flagged ? 'text-red-600' : ''}`}>
                  {eventTypes.reduce((sum, [, rows]) => sum + rows.filter(r => r.flagged).length, 0)}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Cancel in updated?</p>
                <Badge variant={results.has_cancel_in_booking_updated ? 'default' : 'secondary'}>
                  {results.has_cancel_in_booking_updated ? 'yes' : 'no'}
                </Badge>
              </div>
            </div>

            {/* Verdict */}
            {results.verdict === 'green' ? (
              <div className="flex items-start gap-2 rounded-lg border border-green-500 bg-green-50 dark:bg-green-950/20 p-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-green-700 dark:text-green-400">
                    Cancel signals found in booking_updated payloads.
                  </p>
                  <p className="text-muted-foreground mt-1">{results.verdict_detail}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500 bg-amber-50 dark:bg-amber-950/20 p-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-700 dark:text-amber-400">
                    No cancel signals in booking_updated payloads.
                  </p>
                  <p className="text-muted-foreground mt-1">{results.verdict_detail}</p>
                </div>
              </div>
            )}

            {/* Table per event type */}
            {eventTypes.map(([eventName, rows]) => (
              <div key={eventName} className="space-y-2">
                <h3 className="text-sm font-semibold font-mono">{eventName}</h3>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-3 py-2 font-medium">Status value</th>
                        <th className="text-right px-3 py-2 font-medium">Count</th>
                        <th className="text-left px-3 py-2 font-medium">Sample IDs</th>
                        <th className="text-left px-3 py-2 font-medium">Flag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr
                          key={r.payload_status}
                          className={`border-b last:border-0 ${r.flagged ? 'bg-red-50 dark:bg-red-950/20' : ''}`}
                        >
                          <td className="px-3 py-2 font-mono text-xs">{r.payload_status}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                          <td className="px-3 py-2 text-xs space-y-0.5">
                            {r.samples?.map((s) => (
                              <div key={s.uplisting_id} className="flex gap-2">
                                <span className="font-mono">{s.uplisting_id}</span>
                                <span className="text-muted-foreground">{s.guest_name}</span>
                                <span className="text-muted-foreground">{s.check_in}</span>
                                <Badge variant="outline" className="text-[10px] h-4">
                                  {s.db_status}
                                </Badge>
                              </div>
                            ))}
                          </td>
                          <td className="px-3 py-2">
                            {r.flagged && (
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
